import React from 'react';
import { RotateCcwIcon } from 'lucide-react';
import { RecoveryRules, defaultRecoveryRules, recoveryChannels, rulesMatchDefaults } from '../data/recoveryRules';
type SubmissionRulesProps = {
  batchName: string;
  brand?: string;
  rules: RecoveryRules;
  onChange: (rules: RecoveryRules) => void;
  onReset: () => void;
};
export function SubmissionRules({
  batchName,
  brand,
  rules,
  onChange,
  onReset
}: SubmissionRulesProps) {
  const inherited = rulesMatchDefaults(rules, brand);
  const toggleChannel = (id: string) => onChange({
    ...rules,
    channels: rules.channels.includes(id) ? rules.channels.filter(channel => channel !== id) : [...rules.channels, id]
  });
  return <section className="rounded-2xl border border-[#BB5E40]/20 bg-cream/50 p-5" aria-label={`Recovery rules for ${batchName}`} data-id="element-2261" data-mp="2ndl75d8qdvht">
      <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-2262" data-mp="29fo88a6t1czz">
        <div data-id="element-2263" data-mp="2edy3eksj4dll">
          <h3 className="text-sm font-semibold" data-id="element-2264" data-mp="2ecemcrz1tyir">Recovery rules for {batchName}</h3>
          <p className="mt-1 text-xs text-ink/55" data-id="element-2265" data-mp="2weara6hj9e4r">
            {inherited ? 'Inheriting your account defaults from Settings. Any change here applies to this upload only.' : 'Custom rules for this upload. Your account defaults are unchanged.'}
          </p>
        </div>
        {!inherited && <button type="button" onClick={onReset} className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 px-3 py-1.5 text-xs font-semibold text-ink/65 transition-colors hover:bg-paper" data-id="element-2266" data-mp="31f8b0ni6yg8d">
            <RotateCcwIcon className="h-3 w-3" aria-hidden="true" data-id="element-2267" data-mp="2ndvtw8rao8kw" />
            Reset to defaults
          </button>}
      </div>

      <div className="mt-5 grid gap-6 lg:grid-cols-[1.1fr_0.9fr]" data-id="element-2268" data-mp="2ime1wa791ic2">
        <div data-id="element-2269" data-mp="1uxl6ceujhchz">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-2270" data-mp="1ux314spwfnxx">Approved channels</p>
          <div className="mt-3 flex flex-wrap gap-2" data-id="element-2271" data-mp="1pxqf2a6uqkwe">
            {recoveryChannels.map(channel => {
            const active = rules.channels.includes(channel.id);
            return <button key={channel.id} type="button" role="switch" aria-checked={active} onClick={() => toggleChannel(channel.id)} className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-semibold transition-colors ${active ? 'border-[#BB5E40] bg-[#BB5E40] text-cream' : 'border-ink/15 bg-paper text-ink/60 hover:border-[#BB5E40]/40'}`} data-id="element-2272" data-mp="3uzb0crbdx0pj">
                  {channel.iconUrl && <span aria-hidden="true" className={`h-3.5 w-3.5 shrink-0 ${active ? 'bg-cream' : 'bg-ink/60'}`} style={{
                WebkitMaskImage: `url(${channel.iconUrl})`,
                maskImage: `url(${channel.iconUrl})`,
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
                maskPosition: 'center',
                WebkitMaskSize: 'contain',
                maskSize: 'contain'
              }} data-id="element-2273" data-mp="3l1ohxuuvmr0t" />}
                  {channel.name}
                </button>;
          })}
          </div>
          {rules.channels.length === 0 && <p className="mt-3 text-xs font-medium text-[#94452E]" data-id="element-2274" data-mp="mpa2puxsvs13">Select at least one channel or this upload cannot be activated.</p>}
        </div>

        <div className="space-y-5" data-id="element-2275" data-mp="dpmzr4pfae5x">
          <RuleSlider label="Public pricing limit" detail="The maximum discount from MSRP at which this upload can be publicly listed." value={rules.indexPrice} min={0} max={90} suffix="% OFF MSRP" onChange={indexPrice => onChange({
          ...rules,
          indexPrice
        })} data-id="element-2276" data-mp="dqolq47z8l2f" />
          <RuleSlider label="Hold live selling until" detail="Live selling moves units faster but recovers less." value={rules.liveSellingAfterMonth} min={0} max={12} suffix={rules.liveSellingAfterMonth === 0 ? 'available immediately' : 'months in'} onChange={liveSellingAfterMonth => onChange({
          ...rules,
          liveSellingAfterMonth
        })} data-id="element-2277" data-mp="2k5nwgcazbih6" />
        </div>
      </div>
    </section>;
}
function RuleSlider({
  label,
  detail,
  value,
  min,
  max,
  suffix,
  onChange
}: {
  label: string;
  detail: string;
  value: number;
  min: number;
  max: number;
  suffix: string;
  onChange: (value: number) => void;
}) {
  const id = `rule-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return <div data-id="element-2278" data-mp="1djbt1gnjs029">
      <div className="flex items-baseline justify-between gap-3" data-id="element-2279" data-mp="1djlb8i2cp2xe">
        <label htmlFor={id} className="text-sm font-semibold" data-id="element-2280" data-mp="1ifsiqfaeamhg">{label}</label>
        <span className="text-sm font-semibold tabular-nums text-[#94452E]" data-id="element-2281" data-mp="yvl8qimh9m0y">
          {value}
          <span className="ml-1 text-xs font-medium text-ink/50" data-id="element-2282" data-mp="twh0bu7dnsbo">{suffix}</span>
        </span>
      </div>
      <input id={id} type="range" min={min} max={max} value={value} onChange={event => onChange(Number(event.target.value))} className="mt-2 w-full accent-[#BB5E40]" data-id="element-2283" data-mp="17w9kt7sb9cb7" />
      <p className="mt-1 text-xs text-ink/50" data-id="element-2284" data-mp="1hrjmtvipljoz">{detail}</p>
    </div>;
}
export { defaultRecoveryRules };